// BUILD-046 correction cycle 1bb88b3e (stage B2b, items N5 and N6): the small "History" disclosure of a task row.
//
// It reads GET /work/{id}/history (read only, nothing is written) and lists what happened to the task and its NOTES, oldest first: each note attributed and
// dated ("<date> - Note by <name> (recorded <date>)") with its text, so a completion note recorded with Done with note can be seen. A note whose
// text the viewer may not read says so politely. The server decides who may read the history; a refusal is shown as the server's words. The wording is
// historyModel.ts (tested). It opens from the row's "More actions" menu ("History") and closes with its own Close button or Escape.
//
// Owner direction 2026-10-22: ONE LINE PER ENTRY, using the space to the right - "<date> - Edited by <name>", then the Task due date (and the other fields that changed), then the reason - instead of
// several rows under each other. A part that is too long for the window is cut off with an ellipsis (its full wording is its tooltip and stays in the page for assistive technology).
import { useCallback, useEffect, useRef, useState } from 'react';
import { workApi } from '../../api/workShared';
import { isStaleOutcome } from './listStateModel';
import { describeHistory, historyFailed, linePartsOf } from './historyModel';
import type { HistoryLoad } from './historyModel';
// D1 display cleanup: the actor of a line is First name + last initial; the full name is on hover and keyboard focus.
import { NameTipText } from './PersonName';

export interface TaskHistoryPanelProps {
  taskId: string;
  title: string;
  onClose: () => void;
}

export function TaskHistoryPanel({ taskId, title, onClose }: TaskHistoryPanelProps) {
  const [load, setLoad] = useState<HistoryLoad>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const sectionRef = useRef<HTMLElement | null>(null);

  // The panel opens under a row that may be near the bottom of the window: bring it into view when it opens and again once its entries have arrived.
  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [load.phase]);

  useEffect(() => {
    let alive = true;
    setLoad({ phase: 'loading' });
    workApi.getHistory(taskId).then(
      (answer) => {
        if (alive) setLoad({ phase: 'ready', view: describeHistory(answer.history, answer.truncated) });
      },
      (thrown: unknown) => {
        if (alive && !isStaleOutcome(thrown)) setLoad(historyFailed(thrown));
      }
    );
    return () => {
      alive = false;
    };
  }, [taskId, attempt]);

  return (
    <section
      ref={sectionRef}
      aria-label={`History: ${title}`}
      data-testid="task-history"
      className="mt-2 w-full space-y-2 rounded border border-gray-200 bg-gray-50 p-3 text-xs"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-800">History</h4>
        <button type="button" onClick={onClose} aria-label={`Close history: ${title}`} className="rounded px-2 py-0.5 text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]">
          Close
        </button>
      </div>
      {load.phase === 'loading' && (
        <p role="status" aria-live="polite" className="text-gray-600">
          Loading history…
        </p>
      )}
      {load.phase === 'error' && (
        <p role="alert" className="text-red-700">
          {load.message}{' '}
          <button type="button" onClick={retry} aria-label="Retry loading history" className="underline">
            Retry
          </button>
        </p>
      )}
      {load.phase === 'ready' && (
        <>
          {load.view.emptyText !== null && <p className="text-gray-600">{load.view.emptyText}</p>}
          {load.view.rows.length > 0 && (
            <ol className="divide-y divide-gray-200 rounded border border-gray-200 bg-white" data-testid="history-lines">
              {load.view.rows.map((row) => {
                const parts = linePartsOf(row);
                const lastWords = parts.reduce((at, part, index) => (part.kind === 'reason' || part.kind === 'text' ? index : at), -1);
                return (
                  <li key={row.id} data-kind={row.kind} data-testid="history-line" className={`flex min-w-0 flex-nowrap items-baseline gap-x-3 overflow-hidden whitespace-nowrap px-2 py-1 ${row.kind === 'note' ? 'text-gray-900' : 'text-gray-600'}`}>
                    {parts.map((part, index) => (
                      <span
                        key={part.key}
                        data-part={part.kind}
                        title={part.kind === 'headline' ? undefined : part.title}
                        className={[
                          index === 0 ? 'shrink-0' : 'border-l border-gray-300 pl-3',
                          part.kind === 'headline' && row.kind === 'note' ? 'font-medium' : '',
                          part.kind === 'tag' ? 'shrink-0 text-[11px] text-gray-500' : '',
                          index === lastWords ? 'min-w-0 flex-1 truncate' : part.kind === 'tag' || index === 0 ? '' : 'min-w-0 truncate',
                        ].join(' ')}
                      >
                        {index > 0 && <span className="sr-only">; </span>}
                        {part.kind === 'headline' ? <NameTipText text={part.text} fullText={part.full} /> : (<>{part.label !== null && <span className="text-gray-500">{part.label} </span>}{part.text}</>)}
                      </span>
                    ))}
                  </li>
                );
              })}
            </ol>
          )}
          {load.view.truncatedText !== null && <p className="text-gray-500">{load.view.truncatedText}</p>}
        </>
      )}
    </section>
  );
}
